<template>
  <div class="grid-parcelas-container">
    <!-- Grid de Parcelas -->
    <g-data-table
      class="data-table-parcelas"
      Titulo="Parcelas do Pedido"
      v-on:acaoAdicionar="abrirDialogNovaParcela"
      :colunas="colunasParcelas"
      :valores="parcelas"
      :paginacao="false"
      :esconderColunaFrozen="true"
    >
      <!-- Colunas Manuais Customizadas -->
      <template #colunasManuais>
        <!-- Coluna de Data Carregamento Suprema (Formatada) -->
        <PColumn field="dataCarregamentoSuprema" header="Data Carregamento Suprema" :sortable="true">
          <template #body="{ data }">
            <span>{{ formatarData(data.dataCarregamentoSuprema) }}</span>
          </template>
        </PColumn>

        <!-- Coluna de Data Entrega EDITÁVEL -->
        <PColumn field="dataEntregaImpressaoJauense" header="Data Entrega Jauense" :sortable="true">
          <template #body="{ data }">
            <div class="data-entrega-container-editavel">
              <g-date-input
                :modelValue="converterDataParaInput(data.dataEntregaImpressaoJauense)"
                :alturainput="'35px'"
                :largurainput="'120px'"
                :tamanhobotao="'35px'"
                placeholderinput="dd/mm/aaaa"
                :dataPassadas="true"
                @update:modelValue="atualizarDataEntrega(data, $event)"
              />
              <i
                v-if="data.possuiDivergenciaData"
                class="pi pi-exclamation-triangle icone-divergencia"
                v-tooltip.top="'Divergência: data de entrega diverge da data de carregamento'"
              ></i>
            </div>
          </template>
        </PColumn>

        <!-- Coluna de Número da Ordem de Serviço -->
        <PColumn field="numeroOrdemServico" header="Nº OS" :sortable="true">
          <template #body="{ data }">
            <span>{{ data.numeroOrdemServico || '-' }}</span>
          </template>
        </PColumn>
      </template>
    </g-data-table>

    <!-- Resumo das Parcelas -->
    <div class="resumo-parcelas">
      <div class="resumo-item">
        <strong>Total de Parcelas:</strong>
        <span class="resumo-valor">{{ parcelas.length }}</span>
      </div>
      <div class="resumo-item">
        <strong>Qtd Total Folhas:</strong>
        <span class="resumo-valor" :class="{ 'erro': !validarQuantidadeTotal() }">
          {{ calcularTotalCaixas() }} / {{ quantidadeTotalPedido }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import gDateInput from '@/components/g-date-input/g-date-input.vue'
import { useToast } from 'primevue/usetoast'
import PColumn from 'primevue/column'

export default {
  name: 'GridParcelas',

  components: {
    gDateInput,
    PColumn
  },

  setup() {
    const toast = useToast()
    return { toast }
  },

  props: {
    parcelas: {
      type: Array,
      default: () => []
    },
    quantidadeTotalPedido: {
      type: Number,
      required: true
    }
  },

  emits: ['adicionar-parcela', 'editar-parcela', 'excluir-parcela'],

  mounted() {
    console.log('GridParcelas - Parcelas no mounted:', this.parcelas)
  },

  watch: {
    parcelas: {
      deep: true,
      handler(novasParcelas) {
        console.log('GridParcelas - Parcelas atualizadas:', novasParcelas)
        if (novasParcelas.length > 0) {
          console.log('Primeira parcela:', novasParcelas[0])
          console.log('Data Carregamento:', novasParcelas[0].dataCarregamentoSuprema)
          console.log('Data Entrega:', novasParcelas[0].dataEntregaImpressaoJauense)
        }
      }
    }
  },

  data() {
    return {
      colunasParcelas: [
        { field: 'numeroParcela', header: 'Nº Parcela', sortable: true },
        { field: 'quantidadeCaixasSuprema', header: 'Qtd Caixas Suprema', sortable: true },
        { field: 'quantidadeCaixas', header: 'Qtd Folhas Jauense', sortable: true }
        // dataCarregamentoSuprema, numeroOrdemServico, dataEntregaImpressaoJauense, percentualParcela e acoes são adicionadas manualmente via slot colunasManuais
      ]
    }
  },

  methods: {
    /**
     * Formata data para pt-BR
     * @param {Date|string} data - Data a ser formatada
     * @returns {string} Data formatada ou '-'
     */
    formatarData(data) {
      if (!data) return '-'

      try {
        // Se já é string no formato dd/mm/yyyy, retornar direto
        if (typeof data === 'string' && data.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          return data
        }

        // Se for Date ou outro formato, converter
        const dataObj = new Date(data)
        if (isNaN(dataObj.getTime())) {
          return '-'
        }
        return dataObj.toLocaleDateString('pt-BR')
      } catch (error) {
        console.error('Erro ao formatar data:', error)
        return '-'
      }
    },

    /**
     * Atualiza data de entrega da parcela e valida divergências
     * Alerta sobre divergência mas permite continuar
     * @param {Object} parcela - Parcela sendo editada
     * @param {string} novaData - Nova data em formato dd/mm/yyyy
     */
    atualizarDataEntrega(parcela, novaData) {
      // Atualizar a data da parcela
      parcela.dataEntregaImpressaoJauense = novaData

      // Validar divergência de datas
      if (parcela.dataCarregamentoSuprema && novaData) {
        const dataEntrega = this.converterDataParaObjeto(novaData)
        const dataCarregamento = new Date(parcela.dataCarregamentoSuprema)

        // Divergência = data entrega >= data carregamento
        parcela.possuiDivergenciaData = dataEntrega > dataCarregamento
      } else {
        parcela.possuiDivergenciaData = false
      }

      // Emitir evento para o componente pai salvar
      this.$emit('editar-parcela', parcela)

      // Exibir feedback sobre divergência (informativo, não bloqueante)
      if (parcela.possuiDivergenciaData) {
        this.toast.add({
          severity: 'warn',
          summary: 'Divergência de Data',
          detail: 'A data de entrega diverge da data de carregamento',
          life: 3000
        })
      }
    },

    /**
     * Converte string de data pt-BR para objeto Date
     * @param {string} dataStr - Data em formato dd/mm/yyyy
     * @returns {Date} Objeto Date
     */
    converterDataParaObjeto(dataStr) {
      if (!dataStr) return null

      try {
        // Se já for um objeto Date, retornar
        if (dataStr instanceof Date) {
          return dataStr
        }

        // Se for string no formato dd/mm/yyyy
        const partes = dataStr.split('/')
        if (partes.length === 3) {
          return new Date(partes[2], partes[1] - 1, partes[0])
        }

        // Tentar criar Date diretamente
        return new Date(dataStr)
      } catch (error) {
        console.error('Erro ao converter data:', error)
        return null
      }
    },

    /**
     * Converte data para formato adequado ao g-date-input
     * @param {Date|string} data - Data a ser convertida
     * @returns {string} Data em formato dd/mm/yyyy
     */
    converterDataParaInput(data) {
      if (!data) return ''

      try {
        // Se já for string no formato correto, retornar
        if (typeof data === 'string' && data.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          return data
        }

        // Se for Date ou string ISO, formatar
        const dataObj = data instanceof Date ? data : new Date(data)
        return dataObj.toLocaleDateString('pt-BR')
      } catch (error) {
        console.error('Erro ao converter data para input:', error)
        return ''
      }
    },

    /**
     * Calcula o total de caixas de todas as parcelas
     * @returns {number} Total de caixas
     */
    calcularTotalCaixas() {
      return this.parcelas.reduce((sum, p) => sum + (p.quantidadeCaixas || 0), 0)
    },

    /**
     * Valida se a quantidade total de caixas bate com o pedido
     * @returns {boolean} True se válido
     */
    validarQuantidadeTotal() {
      if (this.parcelas.length === 0) return true

      const total = this.calcularTotalCaixas()
      return total === this.quantidadeTotalPedido
    },

    /**
     * Emite evento para abrir dialog de nova parcela
     */
    abrirDialogNovaParcela() {
      this.$emit('adicionar-parcela')
    },

    /**
     * Emite evento para editar parcela
     * @param {Object} parcela - Dados da parcela a editar
     */
    editarParcela(parcela) {
      this.$emit('editar-parcela', parcela)
    },

    /**
     * Confirma exclusão de parcela e emite evento
     * @param {Object} parcela - Dados da parcela a excluir
     */
    confirmarExclusaoParcela(parcela) {
      this.$confirm.require({
        message: `Deseja realmente excluir a parcela ${parcela.numeroParcela}?`,
        header: 'Confirmação',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim',
        rejectLabel: 'Não',
        accept: () => {
          this.$emit('excluir-parcela', parcela)
        }
      })
    }
  }
}
</script>

<style scoped>
.grid-parcelas-container {
  margin-top: 20px;
}

.data-entrega-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.data-entrega-container-editavel {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.data-entrega-container-editavel :deep(.g-date-input) {
  width: auto;
}

.icone-divergencia {
  color: #f59e0b;
  font-size: 1.2rem;
  cursor: help;
}

.resumo-parcelas {
  display: flex;
  justify-content: space-around;
  margin-top: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.resumo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.resumo-item strong {
  color: #495057;
  font-size: 0.9rem;
}

.resumo-valor {
  font-size: 1.2rem;
  font-weight: 600;
  color: #28a745;
}

.resumo-valor.erro {
  color: #e74c3c;
  font-weight: 700;
}

/* Estilo para botões de ação */
.p-button-text {
  width: 2rem;
  height: 2rem;
}

.data-table-parcelas {
  width: 100%;
}
</style>
